import * as React from 'react';

import { cn } from '../../lib/utils';

import type { VariableTextareaProps } from './types';
import {
  getVariableValue,
  parseContent,
  renderTemplate,
  serializeContent,
} from './useContentEditable';
import { VariableChip } from './VariableChip';

export const VariableTextarea = React.forwardRef<
  HTMLDivElement,
  VariableTextareaProps
>(
  (
    {
      value,
      variableDefinitions,
      isDisabled = false,
      isReadOnly = false,
      className,
      id,
      onChange,
      onClick,
      onKeyDown,
      onVariableValueChange,
    },
    ref,
  ) => {
    // Variables
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const lastReportedValueRef = React.useRef(value);

    // State
    const [renderState, setRenderState] = React.useState({
      value: value,
      mountKey: 0,
    });

    const [variableValues, setVariableValues] = React.useState<
      Record<string, string>
    >(() => {
      const values: Record<string, string> = {};
      for (const def of variableDefinitions) {
        values[def.varName] = def.defaultValue;
      }
      return values;
    });

    // Sync variableValues when variableDefinitions change (e.g., when switching notes)
    React.useEffect(() => {
      setVariableValues((prevValues) => {
        const newValues: Record<string, string> = {};
        for (const def of variableDefinitions) {
          // Preserve existing value if it's still valid, otherwise use default
          const existingValue = prevValues[def.varName];
          if (existingValue != null && def.allValues.includes(existingValue)) {
            newValues[def.varName] = existingValue;
          } else {
            newValues[def.varName] = def.defaultValue;
          }
        }
        return newValues;
      });
    }, [variableDefinitions]);

    // Functions
    const combinedRef = React.useCallback(
      (instance: HTMLDivElement | null) => {
        containerRef.current = instance;
        if (typeof ref === 'function') {
          ref(instance);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current =
            instance;
        }
      },
      [ref],
    );

    const segments = React.useMemo(
      () => parseContent(renderState.value),
      [renderState.value],
    );

    const handleInput = React.useCallback(() => {
      const newContent = serializeContent(containerRef.current);
      if (newContent !== lastReportedValueRef.current) {
        lastReportedValueRef.current = newContent;
        onChange(newContent);
      }
    }, [onChange]);

    const handleVariableChange = React.useCallback(
      (varName: string, newValue: string) => {
        setVariableValues((prev) => ({ ...prev, [varName]: newValue }));
        onVariableValueChange?.(varName, newValue);
      },
      [onVariableValueChange],
    );

    // Handle copy: compute rendered text with current variable values
    // This ensures the copy text matches what's visually displayed
    const handleCopy = React.useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        // Build a map of variable values for rendering
        const variableValueMap: Record<string, string> = {};
        for (const def of variableDefinitions) {
          variableValueMap[def.varName] = getVariableValue(
            def.varName,
            variableValues,
            variableDefinitions,
          );
        }

        // Render the template with current variable values
        const rendered = renderTemplate(renderState.value, variableValueMap);
        e.clipboardData.setData('text/plain', rendered);
        e.preventDefault();
      },
      [renderState.value, variableValues, variableDefinitions],
    );

    const renderContent = () =>
      segments.map((segment, index) => {
        if (segment.type === 'variable' && segment.varName) {
          const definition = variableDefinitions.find(
            (d) => d.varName === segment.varName,
          );
          if (definition) {
            const currentValue = getVariableValue(
              segment.varName,
              variableValues,
              variableDefinitions,
            );

            // If only one option, render as plain text instead of dropdown
            if (definition.allValues.length === 1) {
              return (
                <span
                  key={`${segment.varName}-${index}`}
                  data-variable={segment.varName}
                  contentEditable={false}
                >
                  {currentValue}
                </span>
              );
            }

            // Multiple options - render as dropdown chip
            return (
              <VariableChip
                key={`${segment.varName}-${index}`}
                varName={segment.varName}
                currentValue={currentValue}
                allValues={definition.allValues}
                onValueChange={handleVariableChange}
                isDisabled={isDisabled || isReadOnly}
              />
            );
          }
          return <span key={index}>{segment.content}</span>;
        }

        const lines = segment.content.split('\n');
        return lines.map((line, lineIndex) => (
          <React.Fragment key={`${index}-${lineIndex}`}>
            {lineIndex > 0 && <br />}
            {line}
          </React.Fragment>
        ));
      });

    // Detect external changes: value prop differs from what we last reported
    React.useEffect(() => {
      if (value !== lastReportedValueRef.current) {
        // External change detected - update state and increment key to force remount
        lastReportedValueRef.current = value;
        setRenderState((prev) => ({
          value: value,
          mountKey: prev.mountKey + 1,
        }));
      }
    }, [value]);

    return (
      <div
        key={renderState.mountKey}
        ref={combinedRef}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-disabled={isDisabled}
        aria-readonly={isReadOnly}
        tabIndex={isDisabled ? -1 : 0}
        contentEditable={!isDisabled && !isReadOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onCopy={handleCopy}
        className={cn(
          'vai-min-h-[80px] vai-w-full vai-rounded-md vai-border vai-border-input',
          'vai-bg-background vai-px-3 vai-py-2 vai-text-base',
          'vai-ring-offset-background',
          'focus-visible:vai-outline-none focus-visible:vai-ring-2',
          'focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2',
          'md:vai-text-sm',
          'vai-whitespace-pre-wrap vai-break-words',
          isDisabled && 'vai-cursor-not-allowed vai-opacity-50',
          isReadOnly && 'vai-bg-gray-100 vai-cursor-not-allowed',
          className,
        )}
      >
        {renderContent()}
      </div>
    );
  },
);
